/**
 * useAutoPanAfterDelete - Handles auto-panning to nearest node after deletion
 */

import { useRef, useCallback } from 'react';
import { hasAnyFullyVisibleNode, findNearestNodeToViewport, calculateCenteredPan } from './utils/viewResetUtils';

export function useAutoPanAfterDelete({
  tasks,
  nodePositions,
  nodeDimensions,
  pan,
  zoom,
  containerRef,
  setPanAnimated,
}) {
  // Keep refs for values needed in after-delete handler
  const nodePositionsRef = useRef(nodePositions);
  const nodeDimensionsRef = useRef(nodeDimensions);
  const tasksRef = useRef(tasks);
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const containerRefRef = useRef(containerRef);
  const setPanAnimatedRef = useRef(setPanAnimated);
  
  // Update refs on every render (these will be overwritten by useFlowchartCore with correct values)
  nodePositionsRef.current = nodePositions;
  nodeDimensionsRef.current = nodeDimensions;
  tasksRef.current = tasks;
  panRef.current = pan;
  zoomRef.current = zoom;
  containerRefRef.current = containerRef;
  setPanAnimatedRef.current = setPanAnimated;

  // Store refs in a single object ref so we can update it and the callback always sees latest
  const refsRef = useRef({
    tasks: tasksRef,
    nodePositions: nodePositionsRef,
    nodeDimensions: nodeDimensionsRef,
    pan: panRef,
    zoom: zoomRef,
    containerRef: containerRefRef,
    setPanAnimated: setPanAnimatedRef,
  });
  
  // Update the refs object on every render
  refsRef.current = {
    tasks: tasksRef,
    nodePositions: nodePositionsRef,
    nodeDimensions: nodeDimensionsRef,
    pan: panRef,
    zoom: zoomRef,
    containerRef: containerRefRef,
    setPanAnimated: setPanAnimatedRef,
  };

  // Direct callback - uses refsRef to always get latest values
  const handleAfterDelete = useCallback((deletedIds) => {
    const refs = refsRef.current;
    console.log('[AutoPan] handleAfterDelete called with deletedIds:', deletedIds);
    
    // Get remaining tasks (excluding deleted ones)
    const deletedSet = new Set(deletedIds);
    const remainingTasks = refs.tasks.current.filter(t => !deletedSet.has(t.id));
    
    console.log('[AutoPan] remainingTasks:', remainingTasks.length);
    
    // If no tasks remain, no need to pan
    if (remainingTasks.length === 0) {
      console.log('[AutoPan] No remaining tasks, skipping');
      return;
    }
    
    // Get viewport dimensions - find the flowchart container directly from DOM
    // This is more reliable than passing refs through multiple layers
    const container = document.querySelector('.flowchart');
    console.log('[AutoPan] container:', container);
    if (!container) {
      console.log('[AutoPan] No container found in DOM, skipping');
      return;
    }
    
    const containerRect = container.getBoundingClientRect();
    const viewportWidth = containerRect.width;
    const viewportHeight = containerRect.height;
    
    // Get pan/zoom from the canvas transform - more reliable than refs
    const canvas = container.querySelector('.flowchart__canvas');
    let currentPan = refs.pan.current;
    let currentZoom = refs.zoom.current;
    
    if (canvas) {
      const transform = canvas.style.transform;
      // Parse transform: "translate(Xpx, Ypx) scale(Z)"
      const translateMatch = transform.match(/translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/);
      const scaleMatch = transform.match(/scale\(([\d.]+)\)/);
      
      if (translateMatch) {
        currentPan = { x: parseFloat(translateMatch[1]), y: parseFloat(translateMatch[2]) };
      }
      if (scaleMatch) {
        currentZoom = parseFloat(scaleMatch[1]);
      }
    }
    
    const positions = refs.nodePositions.current;
    const dimensions = refs.nodeDimensions.current;
    
    console.log('[AutoPan] currentPan:', currentPan, 'currentZoom:', currentZoom, '(from canvas transform)');
    console.log('[AutoPan] positions:', positions);
    console.log('[AutoPan] dimensions:', dimensions);
    console.log('[AutoPan] viewport:', viewportWidth, 'x', viewportHeight);
    
    // Check if any node is fully visible
    const hasVisibleNode = hasAnyFullyVisibleNode(
      remainingTasks,
      positions,
      dimensions,
      currentPan,
      currentZoom,
      viewportWidth,
      viewportHeight
    );
    
    console.log('[AutoPan] hasVisibleNode:', hasVisibleNode);
    
    // If at least one node is fully visible, no need to pan
    if (hasVisibleNode) {
      console.log('[AutoPan] A node is visible, skipping pan');
      return;
    }
    
    // Find the nearest node to the viewport center
    const nearestNode = findNearestNodeToViewport(
      remainingTasks,
      positions,
      dimensions,
      currentPan,
      currentZoom,
      viewportWidth,
      viewportHeight
    );
    
    console.log('[AutoPan] nearestNode:', nearestNode);
    
    if (!nearestNode) {
      console.log('[AutoPan] No nearest node found, skipping');
      return;
    }
    
    // Calculate pan to center on the nearest node
    const nodeCenterX = nearestNode.position.x + nearestNode.dimensions.width / 2;
    const nodeCenterY = nearestNode.position.y + nearestNode.dimensions.height / 2;
    
    const targetPan = calculateCenteredPan({
      contentCenterX: nodeCenterX,
      contentCenterY: nodeCenterY,
      viewportWidth,
      viewportHeight,
      zoom: currentZoom,
    });
    
    console.log('[AutoPan] targetPan:', targetPan);
    
    // Dispatch a custom event that the flowchart can listen to for panning
    // This avoids the complexity of passing functions through refs
    const event = new CustomEvent('flowchartAutoPan', {
      detail: { targetPan, animate: true }
    });
    console.log('[AutoPan] Dispatching flowchartAutoPan event with:', targetPan);
    window.dispatchEvent(event);
  }, []);

  return {
    handleAfterDelete,
    // Expose refs so they can be updated with real values from interaction hook
    panRef,
    zoomRef,
    containerRefRef,
    setPanAnimatedRef,
    nodePositionsRef,
    nodeDimensionsRef,
  };
}

export default useAutoPanAfterDelete;
